import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileSpreadsheet } from '@fortawesome/pro-light-svg-icons/faFileSpreadsheet';

import { useCallback, useContext, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { NumberInput } from 'ui-components/components/number-input';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { AllocationsGridV3 } from './allocations-grid/allocations-grid-v3';
import { BundleInventory } from './bundle';
import { LocationsGridV3 } from './locations-grid/locations-grid-v3';
import { messages as inventoryMessages } from './messages';
import { InventoryHistoryModal } from './modals/history';
import style from './inventory.module.less';
import { useEnableBundledProducts } from '../../../../../feature-flags/hooks/use-enable-bundled-products';
import { useModal } from '../../../../../hooks/use-modal';
import { BundleContext } from '../../../bundle-context';
import { useProductDetailsContext } from '../../../use-product-details-context';
import { messages } from '../../../messages';

export const ProductDetailsInventoryV3 = () => {
  const { isBundle } = useContext(BundleContext);
  const { productId, productSku, userHasPermission, shouldTrackInventory, setShouldTrackInventory, reorderThreshold, setReorderThreshold } =
    useProductDetailsContext();

  const intl = useIntl();
  const shouldShowBundles = useEnableBundledProducts();
  const { isOpen, open, close } = useModal();
  const shouldDisableButton = !productSku || productId === 0;

  const [isReorderThresholdEnabled, setIsReorderThresholdEnabled] = useState((reorderThreshold ?? '') !== '');

  const onToggleReorderThreshold = useCallback(
    (isEnabled: boolean) => {
      setIsReorderThresholdEnabled(isEnabled);
      if (!isEnabled) {
        setReorderThreshold('');
      }
    },
    [setReorderThreshold],
  );

  const onReorderThresholdChange = useCallback(
    (val: number | undefined) => {
      setReorderThreshold(val != null ? String(val) : '');
    },
    [setReorderThreshold],
  );

  const reorderThresholdValue = (reorderThreshold ?? '') !== '' ? parseFloat(reorderThreshold!) : null;

  if (isBundle && shouldShowBundles) {
    return <BundleInventory />;
  }

  return (
    <div className={style['inventory-tab']}>
      <div className={style['content-card']}>
        {/* Preferences */}
        <div className={style['preferences-section']}>
          <Text element='div' textType={TextType.H4} value={messages.productInventoryPreferences} />
          <FlexColumn className={style['preferences-toggles']} gap={Gap.Space8}>
            <CheckboxSlider
              checkboxId={`${productId}-track-inventory`}
              checked={shouldTrackInventory}
              disabled={!userHasPermission}
              labelText={intl.formatMessage(inventoryMessages.trackInventoryLabel)}
              onChange={setShouldTrackInventory}
            />
            <CheckboxSlider
              checkboxId={`${productId}-enable-reorder-threshold`}
              checked={isReorderThresholdEnabled}
              disabled={!userHasPermission}
              labelText={intl.formatMessage(inventoryMessages.enableReorderThresholdLabel)}
              onChange={onToggleReorderThreshold}
            />
            {isReorderThresholdEnabled ? (
              <div className={style['reorder-threshold-row']}>
                <NumberInput
                  className={style['reorder-threshold-input']}
                  disabled={!userHasPermission}
                  id='reorder-threshold-input'
                  onChange={onReorderThresholdChange}
                  value={reorderThresholdValue}
                />
              </div>
            ) : null}
          </FlexColumn>
        </div>

        {/* Warehouses */}
        <LocationsGridV3 />

        {/* View Stock History */}
        <div className={style['stock-history-row']}>
          <Button disabled={shouldDisableButton} onClick={open} theme={THEME_CONSTANTS.link}>
            <FlexRow className={style['icon-wrapper']}>
              <span>
                <FontAwesomeIcon className={style['info-icon']} icon={faFileSpreadsheet} />
              </span>
              <Text className={style['info-text']} textType={TextType.Paragraph} value={inventoryMessages.inventoryHistoryButton} />
            </FlexRow>
          </Button>
        </div>
        {isOpen ? <InventoryHistoryModal onCloseModal={close} /> : null}

        {/* Allocated Orders */}
        <AllocationsGridV3 />
      </div>
    </div>
  );
};
